import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, TextField, Button, IconButton, CircularProgress, Alert, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
} from '@mui/material';
import { Close, CheckCircle } from '@mui/icons-material';
import { invoiceAPI } from '../api';
import { APP_DIALOG, APP_DIALOG_TITLE_SX } from '../utils/dialogStyle';
import { TOUCH } from '../theme/tokens';
import { formatINR } from './common/Money';
import ShareButtons from './ShareButtons';

const rupees = (n) => formatINR(n, { decimals: Number.isInteger(Math.round((Number(n) || 0) * 100) / 100) ? 0 : 2 });

// Same methods the Dues page offers; the API takes the value, the chip shows the label.
const METHODS = [
    { value: 'Cash', label: 'Cash' },
    { value: 'UPI', label: 'UPI' },
    { value: 'Card', label: 'Card' },
    { value: 'BankTransfer', label: 'Bank' },
    { value: 'Cheque', label: 'Cheque' },
    { value: 'Other', label: 'Other' },
];

// "2026-10-09" + the time right now, as a datetime-local value. The payment is dated to the
// day being worked, so a delivery entered the next morning still lands on the right day.
const atNowOn = (isoDate) => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    return `${isoDate}T${hh}:${mm}`;
};

const longDate = (isoDate) => new Date(`${isoDate}T00:00:00`)
    .toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * Collect a payment on the round, from the Daily Ops card. For the bigger customers who pay
 * on delivery rather than at month end. Everything is filled in from the card — who, the day's
 * items and their total — so on the doorstep it is one tap to save. Uses the same payments API
 * as the Dues page, so it shows up there and on the bill like any other payment.
 *
 * customer: a Daily Ops customer row, plus `lines` [{ name, rate, qty, amount }] and `total`.
 * paidToday: what is already recorded for this customer on this day, if anything.
 */
export default function DailyOpsPaymentDialog({ businessId, business, customer, date, paidToday = 0, onClose, onSaved }) {
    const [amount, setAmount] = useState('');
    const [method, setMethod] = useState('Cash');
    const [when, setWhen] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(null);   // record-payment response once saved

    const due = useMemo(() => Math.max(0, (customer?.total || 0) - paidToday), [customer, paidToday]);

    // Reset only when a customer is opened. Saving reloads the day's payments, which moves
    // paidToday — re-running this then would wipe the "Payment saved" screen mid-share.
    useEffect(() => {
        if (!customer) return;
        // What is still owed for the day; a second collection is prefilled with the rest only.
        setAmount(due > 0 ? String(Math.round(due * 100) / 100) : '');
        setMethod('Cash');
        setWhen(atNowOn(date));
        setNotes(`For ${longDate(date)} delivery`);
        setError('');
        setSaved(null);
        setSaving(false);
    }, [customer]);  // eslint-disable-line react-hooks/exhaustive-deps

    const handleSave = async () => {
        const amt = parseFloat(amount);
        if (!(amt > 0)) { setError('Enter the amount received'); return; }
        setSaving(true);
        setError('');
        try {
            const res = await invoiceAPI.recordPayment(businessId, {
                f_business_customer_id: customer.customer_id,
                f_amount: amt,
                f_discount: 0,
                f_time: new Date(when).toISOString(),
                f_payment_method: method,
                f_notes: notes.trim() || null,
                // Marks it as collected on the round, for this delivery day
                f_source: 'daily_ops',
                f_for_date: date,
            });
            setSaved(res.data);
            onSaved && onSaved();
        } catch (e) {
            setError(e.response?.data?.detail || 'Could not save the payment. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // Receipt text, same shape as the one the Dues page sends.
    const receiptText = () => {
        if (!saved) return '';
        const bizName = business?.f_name || 'Your Vendor';
        const bal = Number(saved.new_balance);
        return [
            `✅ Payment Receipt — ${bizName}`,
            '',
            `Customer: ${saved.customer_name}`,
            `Amount Paid: ${rupees(saved.f_amount)}`,
            `Method: ${METHODS.find(m => m.value === saved.f_payment_method)?.label || saved.f_payment_method}`,
            `Date: ${new Date(saved.f_time).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`,
            '',
            bal > 0 ? `Remaining Balance: ${rupees(bal)}` : 'Balance: Cleared ✅',
            '',
            'Thank you!',
            `— ${bizName}`,
            business?.f_phone ? `📞 ${business.f_phone}` : null,
        ].filter(l => l !== null).join('\n');
    };

    // ShareButtons asks for the link at the moment of sharing, which is also the moment to
    // record that the receipt went out — the Dues page shows that as "shared".
    const getShareUrl = async () => {
        try { await invoiceAPI.markPaymentShared(businessId, saved.payment_id); } catch (_) { /* not critical */ }
        const res = await invoiceAPI.getPaymentShareUrl(businessId, saved.payment_id);
        return res.data?.share_url;
    };

    const lines = customer?.lines || [];

    return (
        <Dialog open={!!customer} onClose={saving ? undefined : onClose} {...APP_DIALOG}>
            <DialogTitle sx={{ ...APP_DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight="bold" noWrap>
                        {saved ? 'Payment saved' : 'Collect payment'}
                    </Typography>
                    <Typography variant="caption" noWrap sx={{ display: 'block', opacity: 0.9 }}>
                        {customer?.customer_name}{customer?.customer_mobile ? ` · ${customer.customer_mobile}` : ''}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} disabled={saving} aria-label="Close" sx={{ color: 'inherit' }}>
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 2, pt: '16px !important' }}>
                {saved ? (
                    <>
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            <CheckCircle color="success" sx={{ fontSize: 48 }} />
                            <Typography variant="h6" fontWeight="bold">{rupees(saved.f_amount)} received</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {METHODS.find(m => m.value === saved.f_payment_method)?.label || saved.f_payment_method}
                                {' · '}{new Date(saved.f_time).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                            <Typography variant="body1" fontWeight={700} sx={{ mt: 1 }}
                                color={Number(saved.new_balance) > 0 ? 'warning.dark' : 'success.main'}>
                                {Number(saved.new_balance) > 0 ? `Dues now ${rupees(saved.new_balance)}` : 'No dues ✅'}
                            </Typography>
                        </Box>
                        {customer?.customer_mobile && (
                            <ShareButtons phone={customer.customer_mobile} text={receiptText()} getShareUrl={getShareUrl} />
                        )}
                    </>
                ) : (
                    <>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                        {/* What the amount is for — the day's items, as on the card */}
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={700}
                                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {longDate(date)} items
                            </Typography>
                            {lines.map(l => (
                                <Box key={l.name} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 0.25 }}>
                                    <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                                        {l.name} <Typography component="span" variant="caption" color="text.secondary">{rupees(l.rate)} × {l.qty}</Typography>
                                    </Typography>
                                    <Typography variant="body2">{rupees(l.amount)}</Typography>
                                </Box>
                            ))}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75, pt: 0.75, borderTop: 1, borderColor: 'divider' }}>
                                <Typography variant="body2" fontWeight={700}>Total</Typography>
                                <Typography variant="body2" fontWeight={700}>{rupees(customer?.total)}</Typography>
                            </Box>
                            {paidToday > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="success.main">Already paid</Typography>
                                    <Typography variant="body2" color="success.main">− {rupees(paidToday)}</Typography>
                                </Box>
                            )}
                        </Box>

                        <TextField
                            fullWidth label="Amount received" type="number" value={amount}
                            onChange={e => setAmount(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                            inputProps={{ min: 0, step: 0.01, inputMode: 'decimal', style: { fontSize: '1.25rem', fontWeight: 700 } }}
                            sx={{ mb: 2 }}
                        />

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>Paid by</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                            {METHODS.map(m => (
                                <Chip key={m.value} label={m.label} clickable
                                    color={method === m.value ? 'primary' : 'default'}
                                    variant={method === m.value ? 'filled' : 'outlined'}
                                    onClick={() => setMethod(m.value)}
                                    sx={{ height: TOUCH.dense, minWidth: 64, fontWeight: 600 }} />
                            ))}
                        </Box>

                        <TextField
                            fullWidth label="Date & time" type="datetime-local" value={when}
                            onChange={e => setWhen(e.target.value)}
                            InputLabelProps={{ shrink: true }} sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth label="Note" value={notes}
                            onChange={e => setNotes(e.target.value)}
                        />
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 2, pb: 1.5, borderTop: 1, borderColor: 'divider' }}>
                {saved ? (
                    <Button variant="contained" onClick={onClose} sx={{ minWidth: 120, height: TOUCH.min }}>Done</Button>
                ) : (
                    <>
                        <Button onClick={onClose} disabled={saving}>Cancel</Button>
                        <Button variant="contained" color="success" onClick={handleSave} disabled={saving}
                            sx={{ minWidth: 140, height: TOUCH.min }}>
                            {saving ? <CircularProgress size={20} color="inherit" />
                                : `Save ${parseFloat(amount) > 0 ? rupees(parseFloat(amount)) : ''}`}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
